import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Switch } from "@shared-kernel/primary/shared/shadcn/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared-kernel/primary/shared/shadcn/ui/alert-dialog";

interface Props {
  name: string;
}

export const TeacherPublicVisibilityField = ({ name }: Props) => {
  const {
    control,
    setValue,
    formState: { defaultValues },
  } = useFormContext();
  const [confirmingDeactivation, setConfirmingDeactivation] = useState(false);

  // Only a profile persisted as visible is actually live on the public site; one merely toggled on within
  // this session was never referenced and carries no de-referencing / Google-ranking risk → skip the confirm.
  const wasPubliclyVisible = Boolean(defaultValues?.[name]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const applyChange = (checked: boolean) => setValue(name, checked, { shouldDirty: true });

        const handleCheckedChange = (checked: boolean) => {
          if (!checked && wasPubliclyVisible) {
            setConfirmingDeactivation(true);
            return;
          }
          applyChange(checked);
        };

        return (
          <FormItem className="flex flex-row items-start justify-between gap-4 rounded-lg border p-4">
            <div className="space-y-1">
              <FormLabel>Profil public visible</FormLabel>
              <p className="text-sm text-muted-foreground">Désactiver pour masquer le professeur de la page publique.</p>
            </div>
            <FormControl>
              <Switch checked={field.value} onCheckedChange={handleCheckedChange} />
            </FormControl>

            <AlertDialog open={confirmingDeactivation} onOpenChange={setConfirmingDeactivation}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Désactiver le profil public ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Attention : ce professeur est actuellement visible sur le site public. En le désactivant, son profil sera retiré du site
                    et n'apparaîtra plus en haut des résultats Google. La position gagnée sur Google sera perdue et repartira de zéro en cas
                    de réactivation.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => applyChange(false)}
                  >
                    Désactiver quand même
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </FormItem>
        );
      }}
    />
  );
};
